import React, { useState, useEffect, useRef } from 'react';
import RecipeCard from '../components/RecipeCard';
import { API_BASE_URL, ENABLE_LIBRARY_DETERMINISTIC_API } from '../config';
import RecipeModalGateway from '../components/RecipeModalGateway';
import { hydrateRecipeForModal } from '../utils/recipeModalData';

export default function AllRecipesPage({ isDarkMode, toggleDarkMode }) {
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [error, setError] = useState('');

    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [recipeModalOpen, setRecipeModalOpen] = useState(false);
    const [userProfile, setUserProfile] = useState(null);

    // Pagination states
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const limit = 18; // Daha hızlı ilk yükleme ve daha hafif sonsuz scroll

    // Dynamic Selectors with persistence
    const [portion, setPortion] = useState(() => localStorage.getItem('allRecipes_portion') || '4');
    const [skillLevel, setSkillLevel] = useState(() => localStorage.getItem('allRecipes_skillLevel') || 'Acemi');

    useEffect(() => {
        const fetchProfile = async () => {
            const token = localStorage.getItem('authToken');
            if (!token) return;
            try {
                const res = await fetch(`${API_BASE_URL}/api/profile`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    const profileData = data.profile || data;
                    setUserProfile(profileData);

                    // Setup defaults if not in localStorage
                    if (!localStorage.getItem('allRecipes_portion')) {
                        setPortion(String(profileData?.servingSize || profileData?.kisi_sayisi || '4'));
                    }
                    if (!localStorage.getItem('allRecipes_skillLevel')) {
                        const rawSkill = (profileData?.skillLevel || profileData?.intakeSurvey?.answers?.q2 || 'acemi').toLowerCase();
                        let finalSkill = 'Acemi';
                        if (rawSkill.includes('chef') || rawSkill.includes('sef') || rawSkill.includes('usta')) finalSkill = 'Usta Şef';
                        else if (rawSkill.includes('deneyimli') || rawSkill.includes('orta')) finalSkill = 'Deneyimli';
                        setSkillLevel(finalSkill);
                    }
                }
            } catch (err) {
                console.error('Fetch Profile Error:', err);
            }
        };
        fetchProfile();
    }, []);

        const optionIcons = {
        corba: 'fa-solid fa-mug-hot',
        ana_yemek: 'fa-solid fa-fire-burner',
        yan_yemek: 'fa-solid fa-utensils',
        salata: 'fa-solid fa-leaf',
        meze: 'fa-solid fa-wine-glass',
        kahvalti: 'fa-solid fa-egg',
        tatli: 'fa-solid fa-cake-candles',
        sos: 'fa-solid fa-bowl-food',
        hamur_isi: 'fa-solid fa-stroopwafel'
    };

    // Seçilebilecek servis tipleri (API'ye her zaman kanonik key gönderilir)
    const filterOptions = [
        { key: 'corba', label: 'Çorba' },
        { key: 'ana_yemek', label: 'Ana Yemek' },
        { key: 'yan_yemek', label: 'Yan Yemek' },
        { key: 'salata', label: 'Salata' },
        { key: 'meze', label: 'Meze' },
        { key: 'kahvalti', label: 'Kahvaltılık' },
        { key: 'tatli', label: 'Tatlı' },
        { key: 'sos', label: 'Sos' },
        { key: 'hamur_isi', label: 'Hamur İşi' }
    ];
    const filterLabelByKey = Object.fromEntries(filterOptions.map((o) => [o.key, o.label]));
    const fetchRecipes = async (styles = [], currentPage = 1, isLoadMore = false) => {
        setLoading(true);
        if (!isLoadMore) setError('');

        try {
            const requestStartedAt = performance.now();
            const token = localStorage.getItem('authToken');
            const requestedServeStyles = styles
                .map((style) => String(style || '').trim())
                .filter(Boolean);
            const endpoint = ENABLE_LIBRARY_DETERMINISTIC_API
                ? '/api/recipes/library'
                : '/api/recipes/explore';
            const res = await fetch(`${API_BASE_URL}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    serveStyles: requestedServeStyles,
                    portion: Number(portion) || 1,
                    servingSize: Number(portion) || 1,
                    page: currentPage,
                    limit
                })
            });

            const data = await res.json();
            const requestMs = performance.now() - requestStartedAt;
            console.log(
                `[PERF][UI AllRecipes ${ENABLE_LIBRARY_DETERMINISTIC_API ? 'Library' : 'Explore'}] page=${currentPage} limit=${limit} loadMore=${isLoadMore} took=${requestMs.toFixed(1)}ms route=${res.headers.get('X-BBTV-Route-Total-Ms') || '-'}ms returned=${data?.recipes?.length || 0} total=${data?.total ?? data?.totalCount ?? 0}`
            );
            if (res.ok && data.success) {
                const fetchedRecipes = data.recipes || [];

                if (isLoadMore) {
                    setRecipes(prev => [...prev, ...fetchedRecipes]);
                } else {
                    setRecipes(fetchedRecipes);
                }

                const total = Number(data.total ?? data.totalCount ?? 0);
                const apiHasMore = data.hasMore === true;
                setHasMore(apiHasMore || total > currentPage * limit || fetchedRecipes.length === limit);
            } else {
                if (!isLoadMore) setError(data.error || 'Tarifler alınırken bir sorun oluştu.');
            }
        } catch (err) {
            console.error('Fetch Browse Recipes Error:', err);
            if (!isLoadMore) setError('Ağ hatası veya sunucuya ulaşılamıyor.');
        } finally {
            setLoading(false);
        }
    };

    // İlk açılışta verileri çekme, sadece filtreler değiştiğinde çek
    // Yeni filtre seçildiğinde page sıfırlanmalı
    useEffect(() => {
        if (selectedFilters.length > 0) {
            setPage(1);
            fetchRecipes(selectedFilters, 1, false);
        } else {
            setRecipes([]);
            setHasMore(false);
        }
    }, [selectedFilters, portion]);

    const loadMore = () => {
        if (!loading && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchRecipes(selectedFilters, nextPage, true);
        }
    };

    const [selectedRecipeNames, setSelectedRecipeNames] = useState([]);

    const toggleFilter = (option) => {
        setSelectedFilters(prev =>
            prev.includes(option)
                ? prev.filter(f => f !== option)
                : [...prev, option]
        );
    };

    const toggleSelection = (recipeOrArray) => {
        const recipes = Array.isArray(recipeOrArray) ? recipeOrArray : [recipeOrArray];
        setSelectedRecipeNames(prev => {
            const next = [...prev];
            recipes.forEach(r => {
                const name = r.title || r.name;
                if (!name) return;
                const idx = next.indexOf(name);
                if (idx >= 0) {
                    next.splice(idx, 1);
                } else {
                    next.push(name);
                }
            });
            return next;
        });
    };

    const handleRecipeClick = async (recipe) => {
        setSelectedRecipe(await hydrateRecipeForModal(recipe, {
            targetPortion: Number(portion) || 1
        }));
        setRecipeModalOpen(true);
    };

    return (
        <div className={`w-full h-full min-h-screen ${isDarkMode ? 'text-white' : 'text-slate-900'} bg-transparent flex flex-col items-center pb-20`}>

            {/* Üst Kısım - Minimalist Başlık ve Tercihler */}
            <div className="w-full max-w-7xl px-8 pt-10 pb-6 text-left border-b border-transparent flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <h1 className={`text-4xl md:text-5xl font-black tracking-tight mb-2 ${isDarkMode ? 'text-white' : 'text-[#121629]'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Tarif Kütüphanesi
                    </h1>
                    <p className={`text-sm md:text-base font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Mutfaktaki en iyi yardımcınızla bugün ne pişiriyoruz? İstediğiniz öğünleri tıklayın, sizin için en uygun olan tarifleri listeleyin.
                    </p>
                </div>

                {/* Right/Top: Preferences (Portion & Skill) */}
                <div className="flex flex-col sm:flex-row lg:items-center justify-end gap-4">
                    {/* Portion Selector */}
                    <div className="flex flex-col gap-2">
                        <p className={`text-[10px] font-black uppercase tracking-widest pl-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>KİŞİ</p>
                        <div className="relative h-12 w-full sm:w-32">
                            <input
                                type="number"
                                min={1}
                                value={portion}
                                onChange={e => {
                                    const val = e.target.value;
                                    setPortion(val);
                                    localStorage.setItem('allRecipes_portion', val);
                                }}
                                className={`w-full h-full border rounded-2xl pl-5 pr-10 text-sm font-black focus:border-red-500 transition-all outline-none shadow-sm ${isDarkMode
                                    ? 'bg-[#22242d]/80 text-white border-white/5'
                                    : 'bg-white text-slate-800 border-slate-200'
                                    }`}
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-bold pointer-events-none italic">Kişi</span>
                        </div>
                    </div>

                    {/* Skill Selector */}
                    <div className="flex flex-col gap-2">
                        <p className={`text-[10px] font-black uppercase tracking-widest pl-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>TECRÜBE</p>
                        <div className={`flex p-1 rounded-2xl border shadow-sm h-12 ${isDarkMode ? 'bg-[#22242d]/80 border-white/5' : 'bg-white border-slate-200'
                            }`}>
                            {['Acemi', 'Deneyimli', 'Usta Şef'].map((lvl) => (
                                <button
                                    key={lvl}
                                    onClick={() => {
                                        setSkillLevel(lvl);
                                        localStorage.setItem('allRecipes_skillLevel', lvl);
                                    }}
                                    className={`flex-1 flex items-center justify-center px-4 rounded-xl text-[10px] font-black transition-all ${skillLevel === lvl
                                        ? 'bg-red-500 text-white shadow-md'
                                        : isDarkMode
                                            ? 'text-slate-400 hover:text-slate-200'
                                            : 'text-slate-500 hover:text-slate-800'
                                        }`}
                                >
                                    {lvl.includes('Usta') ? 'Usta' : lvl}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Filtre Kontrolleri */}
            <div className="w-full max-w-7xl px-4 md:px-8 relative z-20 mb-8 mt-2">
                <div className={`rounded-[2rem] p-5 md:p-6 ${isDarkMode ? 'bg-transparent border border-white/5 shadow-none' : 'bg-transparent border border-slate-200/50 shadow-none'}`}>
                    <h2 className={`text-xs font-bold tracking-[0.2em] uppercase mb-5 flex items-center gap-3 ${isDarkMode ? 'text-slate-300' : 'text-[#111827]'}`}>
                        <i className="fa-solid fa-bars-staggered text-red-500 text-sm"></i>
                        KATEGORİ FİLTRESİ
                    </h2>

                    <div className="flex flex-wrap items-center gap-2.5 w-full">
                        {filterOptions.map(option => {
                            const isSelected = selectedFilters.includes(option.key);
                            return (
                                <button
                                    key={option.key}
                                    onClick={() => toggleFilter(option.key)}
                                    className={`
                                        transition-all duration-300 ease-out px-4 py-2 rounded-full font-bold text-xs md:text-sm flex items-center gap-2
                                        hover:-translate-y-0.5 focus:outline-none tracking-wide
                                        ${isSelected
                                            ? 'bg-[#ef4444] text-white shadow-md shadow-red-500/10'
                                            : isDarkMode
                                                ? 'bg-[#22242d]/80 text-slate-300 hover:bg-[#2b2d38] hover:text-white border border-white/5'
                                                : 'bg-white text-[#334155] hover:bg-[#e2e8f0] hover:text-[#0f172a] border border-slate-200'
                                        }
                                    `}
                                >
                                    <i className={`${optionIcons[option.key] || 'fa-solid fa-utensils'} text-[11px] md:text-[13px] ${isSelected ? 'text-white' : 'text-slate-400'}`}></i>
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Sonuç Alanı */}
            <div className="w-full max-w-7xl px-4 pb-20 flex-1 flex flex-col">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 animate-pulse">
                        <div className="w-16 h-16 border-4 border-rose-500/20 border-t-rose-500 rounded-full animate-spin mb-6"></div>
                        <p className={`font-bold text-lg tracking-widest ${isDarkMode ? 'text-rose-200/50' : 'text-rose-600'}`}>TARİFLER DERLENİYOR...</p>
                    </div>
                ) : error ? (
                    <div className={`p-6 rounded-2xl mx-auto w-full max-w-2xl shadow-lg my-10 border ${isDarkMode ? 'bg-red-950/30 border-red-900/50' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex items-start">
                            <span className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-500 mt-1 mr-4 shrink-0">
                                <i className="fa-solid fa-triangle-exclamation text-lg"></i>
                            </span>
                            <div>
                                <h3 className={`font-black text-xl mb-1 ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>Eksik Malzeme / Bağlantı Hatası</h3>
                                <p className={isDarkMode ? 'text-red-300/80' : 'text-red-600/90'}>{error}</p>
                            </div>
                        </div>
                    </div>
                ) : recipes.length > 0 ? (
                    <div className="w-full mt-4">
                        <div className={`flex justify-between items-end mb-8 border-b pb-4 ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                            <h2 className={`text-sm md:text-base font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Mevcut Malzemelerinize ve Profil Bilgilerinize Göre <span className="text-rose-500 font-bold mr-1">{recipes.length}{hasMore ? '+' : ''}</span> Olası Tarif Bulundu
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 hover:gap-8 transition-all duration-500">
                            {recipes.map(recipe => (
                                <RecipeCard
                                    key={recipe.id}
                                    recipe={recipe}
                                    isDarkMode={isDarkMode}
                                    isModern={true}
                                    userProfile={userProfile}
                                    userPortion={parseInt(portion)}
                                    isSelected={selectedRecipeNames.includes(recipe.title || recipe.name)}
                                    onToggleSelect={toggleSelection}
                                    onCardClick={() => handleRecipeClick(recipe)}
                                />
                            ))}
                        </div>

                        {/* Load More Section */}
                        {hasMore && (
                            <div className="w-full flex justify-center mt-12 mb-8">
                                <button
                                    onClick={loadMore}
                                    disabled={loading}
                                    className={`
                                        px-8 py-3 rounded-2xl font-black tracking-wide text-sm transition-all duration-300
                                        ${loading
                                            ? 'opacity-50 cursor-not-allowed bg-slate-200 text-slate-500 border border-slate-300'
                                            : isDarkMode
                                                ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30 hover:bg-rose-500/30 shadow-lg shadow-rose-900/20'
                                                : 'bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 shadow-md'
                                        }
                                    `}
                                >
                                    {loading ? (
                                        <span className="flex items-center gap-2">
                                            <i className="fa-solid fa-spinner fa-spin"></i> Yükleniyor...
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            <i className="fa-solid fa-layer-group"></i> Daha Fazla Tarif Yükle
                                        </span>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                ) : selectedFilters.length > 0 ? (
                    <div className="flex flex-col items-center justify-center py-32 text-center px-4">
                        <div className={`w-28 h-28 rounded-full flex items-center justify-center mb-8 ${isDarkMode ? 'bg-black/20 border border-white/5' : 'bg-white/50 border border-slate-200'}`}>
                            <i className="fa-solid fa-magnifying-glass-chart text-5xl text-rose-500/50"></i>
                        </div>
                        <h3 className={`text-3xl font-black mb-3 ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>Sonuç Bulunamadı</h3>
                        <p className={`text-lg max-w-md ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Seçmiş olduğunuz <b>{selectedFilters.map((f) => filterLabelByKey[f] || f).join(', ')}</b> kategorisi altında kütüphanede aktif bir tarif yer almıyor.
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-32 text-center px-4 flex-1">
                        <div className={`w-36 h-36 rounded-full flex items-center justify-center mb-8 ${isDarkMode ? 'bg-black/10 border border-rose-900/40' : 'bg-rose-50/50 border border-rose-100/50'}`}>
                            <i className={`fa-solid fa-utensils text-7xl ${isDarkMode ? 'text-rose-900/60' : 'text-rose-200/80'} drop-shadow-lg`}></i>
                        </div>
                        <h3 className={`text-4xl font-black mb-4 tracking-tighter ${isDarkMode ? 'text-white' : 'text-slate-800'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>Keşfetmeye Hazır mısınız?</h3>
                        <p className={`text-xl max-w-xl leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Tüm tarifleri buzdolabınızdaki malzemelere göre listeleyip, sağlık, son kullanma tarihi, bütçe ve lezzet sıralamasına göre dökerek dilediğiniz yemek tarifine ulaşabilir, kararınızı verebilirsiniz.
                        </p>
                    </div>
                )}
            </div>

            {/* Modal */}
            {recipeModalOpen && selectedRecipe && (
                <RecipeModalGateway
                    modalVariant="smart"
                    recipe={selectedRecipe}
                    isOpen={recipeModalOpen}
                    isDarkMode={isDarkMode}
                    userProfile={userProfile}
                    userPortion={parseInt(portion)}
                    skillLevel={skillLevel}
                    onClose={() => setRecipeModalOpen(false)}
                    onPairingClick={handleRecipeClick}
                />
            )}
        </div>
    );
}


